import React, { useMemo, useState } from 'react';
import { Attachment } from 'presentation/components/Queue/Chat/Chat';
import { IconClear, IconDownload } from 'components/Icons';
import MessageAttachmentPreview from 'presentation/components/Queue/common/MessageAttachments/MessageAttachmentPreview';
import { MobileSlideUpPanel } from 'presentation/components/Mobile/MobileSlideUpPanel/MobileSlideUpPanel';
import { cleanFilename } from 'presentation/components/Queue/utils/fileUtils';
import './MobileAttachmentModal.scss';

type MobileAttachmentModalProps = {
  attachments: Attachment[] | undefined;
  isOpen: boolean;
  handleClose: () => void;
};

/**
 * Mobile-optimized attachment viewer modal that displays attachments in a slide-up panel
 */
const MobileAttachmentModal: React.FC<MobileAttachmentModalProps> = ({ attachments, isOpen, handleClose }) => {
  if (!attachments || !isOpen) return null;

  const attachment = attachments[0];
  const [currentAttachment, setCurrentAttachment] = useState<Attachment>(attachment);
  const [currentBlob, setCurrentBlob] = useState<Blob | undefined>(undefined);

  const cleanedFilename = useMemo(() => {
    return cleanFilename(currentAttachment.filename);
  }, [currentAttachment]);

  const handleDownload = () => {
    if (!currentBlob) return;

    try {
      const url = URL.createObjectURL(currentBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cleanedFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download failed:', e);
      alert('Failed to download file: ' + (e instanceof Error ? e.message : 'Unknown error'));
    }
  };

  const handleChangeImage = (attachment: Attachment) => {
    setCurrentAttachment(attachment);
  };

  const handleChangeBlob = (blob: Blob) => {
    setCurrentBlob(blob);
  };

  return (
    <MobileSlideUpPanel isOpen={isOpen} onClose={handleClose} title={cleanedFilename} fullSizeOnOpen={true}>
      <div className="mobile-attachment-modal">
        <div className="mobile-attachment-modal__content">
          <div className="mobile-attachment-modal__viewer">
            <MessageAttachmentPreview attachment={currentAttachment} size="normal" />
          </div>

          <div className="mobile-attachment-modal__actions">
            <button className="mobile-attachment-modal__download-button" onClick={handleDownload}>
              <IconDownload />
              <span>Download</span>
            </button>
          </div>

          {attachments.length > 1 && (
            <div className="mobile-attachment-modal__thumbnails">
              {attachments.map((attachment, index) => (
                <div
                  key={index}
                  className={`mobile-attachment-modal__thumbnail ${attachment.filename === currentAttachment.filename ? 'selected' : ''}`}
                >
                  <MessageAttachmentPreview
                    attachment={attachment}
                    size="thumbnail"
                    handleClickPreview={handleChangeImage}
                    handleSetDownloadedBlob={handleChangeBlob}
                    isSelected={attachment.filename === currentAttachment.filename}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileSlideUpPanel>
  );
};

export default MobileAttachmentModal;
